import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation
} from '@angular/core';

import {
  DeviceService, IconSizeEnum, IExtendedLink, IFootNote, ILinkMedia, ImageRatioEnum, IRating,
  SdBannerComponent, SdFootnoteComponent, SdFormIncrementComponent, SdIconComponent,
  SdTagComponent, SdThemeComponent, SdTitleComponent
} from '@seb/web-design-system/core';

import { IDevice, SdDevicesComponent } from '@seb/web-design-system/devices';
import { SdRecipeUserComponent } from '@seb/web-design-system/recipe-user';
import { SdRatingComponent } from '@seb/web-design-system/reviews';

import { IRecipe, IRecipeOverview } from './recipe-overview.interface';
import { SdTimerComponent } from './timer/timer.component';

@Component({
  selector: 'sd-recipe-overview',
  templateUrl: './recipe-overview.component.html',
  styleUrls: ['./recipe-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    SdTagComponent,
    SdBannerComponent,
    SdTitleComponent,
    SdIconComponent,
    SdRatingComponent,
    SdTimerComponent,
    SdDevicesComponent,
    SdFormIncrementComponent,
    SdFootnoteComponent,
    SdRecipeUserComponent,
    TranslateModule
  ]
})
export class SdRecipeOverviewComponent extends SdThemeComponent implements OnInit, OnDestroy {
  @Input()
  recipeOverview: IRecipeOverview;

  @Input()
  isReworked = false;

  @Input()
  isLoading = false;

  @Output()
  printClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  reviewsClicked = new EventEmitter<number>();

  @Output()
  reviewSeeAllClicked = new EventEmitter<void>();

  @Output()
  deviceIsClicked = new EventEmitter<IDevice>();

  @Output()
  devicePushIsClicked = new EventEmitter<IExtendedLink | ILinkMedia>();

  @Output()
  updateValue = new EventEmitter<number>();

  imageRatioEnum = ImageRatioEnum;
  iconSizeEnum = IconSizeEnum;
  isMobile$: boolean;
  subscriptions = new Subscription();
  ingredientsDisclaimer!: string;
  footNotesDisclaimer: IFootNote[];

  constructor(
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ingredientsDisclaimer = this.translateService.instant('recipes.blockIngredients.disclaimer');

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.footNotesDisclaimer = [{ note: '*' + this.ingredientsDisclaimer }];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onPrintClicked(): void {
    this.printClicked.emit();
  }

  ratingData(): IRating {
    return {
      averageRatings: Math.ceil(this.recipeOverview.recipe?.communityTopRecipe?.rating * 10) / 10,
      numberOfReviews: this.recipeOverview.recipe?.communityTopRecipe?.comments
    };
  }

  getYieldOptions(recipe: IRecipe): number[] {
    const yields: number[] = [];

    if (recipe.yield) {
      yields.push(recipe.yield.quantity);
    }

    recipe.variants?.forEach((variant: IRecipe) => {
      if (variant.yield) {
        yields.push(variant.yield.quantity);
      }
    });

    return yields.sort((a, b) => a - b);
  }

  onReviewsClicked(event: number): void {
    this.reviewsClicked.emit(event);
  }

  onReviewsSeeAllClicked(): void {
    this.reviewSeeAllClicked.emit();
  }

  onDeviceIsClicked(event: IDevice): void {
    this.deviceIsClicked.emit(event);
  }

  onDevicePushIsClicked(event: IExtendedLink | ILinkMedia): void {
    this.devicePushIsClicked.emit(event);
  }

  onUpdateValue(event: number): void {
    this.updateValue.emit(event);
  }
}
